<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import { useAccount } from 'dashboard/composables/useAccount';

import ChannelItem from 'dashboard/components/widgets/ChannelItem.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');

// Было: ref({}) + присвоение в onMounted. Так значение снималось ОДИН раз, и
// при заходе прямо на этот адрес данные аккаунта ещё не успевали прийти —
// в компонент уходил undefined, там падал Object.keys, и сетка каналов не
// отрисовывалась совсем: страница «Выберите источник» оставалась пустой, а
// завести новый источник через интерфейс было нельзя. Computed пересчитается,
// когда аккаунт загрузится.
const enabledFeatures = computed(() => currentAccount.value?.features ?? {});

// Скрытые каналы (facebook/whatsapp/instagram) возвращаются добавлением в
// список; line/tiktok/voice кандидаты на выпил — см. AGENTS.md.
// Почта и SMS убраны по решению владельца: почтой мы не занимаемся, а SMS
// в РФ дороги (см. У9) — оба только путали бы при выборе источника.
const channelList = computed(() => {
  const { apiChannelName } = globalConfig.value;
  const channels = [
    {
      key: 'website',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-woot-website',
    },
    {
      key: 'api',
      title: apiChannelName || t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-woot-api',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-woot-telegram',
    },
    {
      key: 'max',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MAX.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MAX.DESCRIPTION'),
      icon: 'i-ri-chat-3-fill',
    },
    {
      key: 'vk',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VK.DESCRIPTION'),
      icon: 'i-ri-vk-fill',
    },
    // Р75.1: форма заявок — свой источник, а не галочка у API.
    {
      key: 'lead_form',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LEAD_FORM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LEAD_FORM.DESCRIPTION'),
      icon: 'i-ri-file-list-3-line',
    },
  ];

  return channels;
});

const initChannelAuth = channel => {
  const params = {
    sub_page: channel,
    accountId: accountId.value,
  };
  router.push({ name: 'settings_inboxes_page_channel', params });
};
</script>

<template>
  <div
    class="grid max-w-3xl grid-cols-1 xs:grid-cols-2 mx-0 gap-6 sm:grid-cols-3 p-8"
  >
    <ChannelItem
      v-for="channel in channelList"
      :key="channel.key"
      :channel="channel"
      :enabled-features="enabledFeatures"
      @channel-item-click="initChannelAuth"
    />
  </div>
</template>
